// ============================================
// Material Registry — built-in materials + per-asset instance cache
// ============================================
//
// Two responsibilities, both pure mechanism (no shader imports — built-in
// factories are registered by the renderer where .wgsl is importable, so this
// module stays headless-testable and bun-gate-safe):
//
//  1. Built-in materials by stable id (engine:sprite-default / sprite-lit /
//     text-msdf / vfx-default). Registered via `registerBuiltin(id, factory)`;
//     constructed lazily + cached per registry.
//  2. A per-GUID instance cache for user .mat assets (so many SpriteRenderers
//     sharing one .mat share one Material). `getOrCreate(guid, factory)`.
//
// `SpriteRenderer.material === ''` resolves to BUILTIN_SPRITE_DEFAULT.

import { type Material } from './Material';
import { type IRenderContext } from '../core/IRenderContext';

/** Stable built-in material ids. */
export const BUILTIN_SPRITE_DEFAULT = 'engine:sprite-default';
export const BUILTIN_SPRITE_LIT = 'engine:sprite-lit';
export const BUILTIN_TEXT_MSDF = 'engine:text-msdf';
export const BUILTIN_VFX_DEFAULT = 'engine:vfx-default';

/** Factory that constructs a built-in material against a render context. */
export type MaterialFactory = (ctx: IRenderContext) => Material;

export class MaterialRegistry {
  private _factories = new Map<string, MaterialFactory>();
  private _builtinCache = new Map<string, Material>();
  private _assetCache = new Map<string, Material>();

  // ---- Built-ins ----

  /** Register (or replace) a built-in material factory by id. */
  registerBuiltin(id: string, factory: MaterialFactory): void {
    this._factories.set(id, factory);
    // Drop any cached instance so the new factory takes effect.
    const cached = this._builtinCache.get(id);
    if (cached) {
      cached.destroy();
      this._builtinCache.delete(id);
    }
  }

  /** True if a built-in factory is registered for `id`. */
  hasBuiltin(id: string): boolean {
    return this._factories.has(id);
  }

  /** All registered built-in ids. */
  listBuiltins(): string[] {
    return [...this._factories.keys()];
  }

  /**
   * Get a built-in material by id, constructing + caching on first use.
   * Returns null if no factory is registered (feature not yet wired).
   */
  getBuiltin(ctx: IRenderContext, id: string): Material | null {
    const cached = this._builtinCache.get(id);
    if (cached) return cached;
    const factory = this._factories.get(id);
    if (!factory) return null;
    const mat = factory(ctx);
    this._builtinCache.set(id, mat);
    return mat;
  }

  // ---- Per-asset (.mat guid) instance cache ----

  /** Cached material instance for a .mat guid, or null. */
  getCached(guid: string): Material | null {
    return this._assetCache.get(guid) ?? null;
  }

  /**
   * Return the cached material for `guid`, or build + cache it via `factory`.
   * `factory` may return null (e.g. shader unresolved) — null is not cached.
   */
  getOrCreate(guid: string, factory: () => Material | null): Material | null {
    const cached = this._assetCache.get(guid);
    if (cached) return cached;
    const mat = factory();
    if (mat) this._assetCache.set(guid, mat);
    return mat;
  }

  /** Drop + destroy the cached material for a .mat guid (e.g. on reimport). */
  invalidate(guid: string): void {
    const mat = this._assetCache.get(guid);
    if (mat) {
      mat.destroy();
      this._assetCache.delete(guid);
    }
  }

  /** Destroy + clear every cached material (built-in and per-asset). */
  clear(): void {
    for (const m of this._builtinCache.values()) m.destroy();
    for (const m of this._assetCache.values()) m.destroy();
    this._builtinCache.clear();
    this._assetCache.clear();
  }
}

/** Process-wide material registry singleton. */
export const materialRegistry = new MaterialRegistry();
